import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar } from "primereact/calendar";
import "primereact/resources/themes/lara-light-cyan/theme.css";
import { RxCaretDown, RxCaretUp } from "react-icons/rx";
import pharmacyIcon from "./../../../../../Assets/pharmacy-icon.svg";
import { FiArrowRight } from "react-icons/fi";
import { FaArrowRight } from "react-icons/fa";
import {
  getPharmacyServiceAvailibility,
  getServiceBookingSlots,
  updateBookingField,
  setSelectedServiceBookingSlot,
  setSelectedBranch,
} from "../../../../../dispatchers/bookingDispatchers";
import useBookingApp from "../../../../../hooks/useBookingApp";
import CustomLoader from "../../../../controls/CustomLoader";
import { saveService } from "../../../../../dispatchers/templateSectionDispatchers";

export default function YourAppointment({
  mainColor,
  handleNextStep,
  enableDateTimeSelection,
  setEnableDateTimeSelection,
}) {
  const { bookingSectionState } = useBookingApp();
  const {
    isBookingSlotLoading,
    selectedBranch,
    selectedPrivateService,
    servicePharmacy,
    bookingModel,
    companyDetails,
    isEnablePrePayment,
  } = bookingSectionState;

  const [openBranchDropdown, setOpenBranchDropdown] = useState(false);
  const [date, setDate] = useState(null);
  const [disableDays, setDisableDays] = useState([]);
  const [availableBookingSlots, setAvailableBookingSlots] = useState(null);
  const [availableDays, setAvailableDays] = useState([]);
  const dayNamesMap = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const allDays = [0, 1, 2, 3, 4, 5, 6]; // Full week

  const BookNowButton = ({ selectedSlot }) => {
    const handleNext = () => {
      if (!isEnablePrePayment) {
        const objectModel = {
          serviceModel: bookingModel,
          serviceSlot: selectedSlot,
        };
        saveService(objectModel);
      }
      setSelectedServiceBookingSlot(selectedSlot);
      handleNextStep();
    };

    return (
      <button className="bf-book-now-btn" style={{ backgroundColor: mainColor }} onClick={handleNext}>
        <p>Book Now</p>
        <FaArrowRight />
      </button>
    );
  };

  const handlePharmacyClick = async (pharmacy) => {
    setSelectedBranch(pharmacy);
    setOpenBranchDropdown(false);
    setEnableDateTimeSelection(true);
    updateBookingField({ field: "PharmacyId", value: pharmacy?.pharmacyID });
    localStorage.setItem("booking-data", JSON.stringify(pharmacy));

    const response = await getPharmacyServiceAvailibility({
      pharmacyId: pharmacy?.pharmacyID,
      serviceID: selectedPrivateService,
    });

    // Extract or compute formatted date here
    const formattedDate = new Date().toISOString().split("T")[0]; // or based on user selection
    updateBookingField({ field: "AppointmentDate", value: formattedDate });

    const bookingSlots = await getServiceBookingSlots({
      pharmacyId: pharmacy?.pharmacyID,
      serviceId: selectedPrivateService,
      date: formattedDate,
    });

    const days = response.map((item) => item.dayOfWeek).sort((a, b) => a - b);
    const disabledDays = allDays.filter((day) => !days.includes(day));
    const dayNames = days.map((day) => dayNamesMap[day]);

    setAvailableDays(dayNames);
    setDisableDays(disabledDays);
    setAvailableBookingSlots(bookingSlots);
  };

  useEffect(() => {
    const persistedPharmacy =
      JSON.parse(localStorage.getItem("booking-data")) || null;

    if (persistedPharmacy != null) {
      setSelectedBranch(persistedPharmacy);
      setEnableDateTimeSelection(true);
    }
  }, []);

  const handleDateSelection = async (selectedDate) => {
    setDate(selectedDate);
    const formattedDate = selectedDate
      .toLocaleDateString("en-CA")
      .split("T")[0];
    updateBookingField({ field: "AppointmentDate", value: formattedDate });
    const bookingSlots = await getServiceBookingSlots({
      pharmacyId: selectedBranch?.pharmacyID,
      serviceId: selectedPrivateService,
      date: formattedDate,
    });
    setAvailableBookingSlots(bookingSlots);
  };

  return (
    <motion.div
      initial={{ y: 50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 100, delay: 0.5 }}
      exit={{ y: 50, opacity: 0 }}
      className="bf-content-wrapper your-booking-wrapper"
    >
      <div className="bf-content-body">
        <div className="bf-choose-branch">
          <div className="bf-content-header">
            <h2 className="bf-title" style={{ color: mainColor }}>
              Choose a Branch
            </h2>
          </div>
          <motion.div
            className="branch-dropdown"
            style={{
              border: `1px solid ${mainColor}`,
            }}
            onClick={() => setOpenBranchDropdown(!openBranchDropdown)}
            animate={{
              maxHeight: openBranchDropdown ? 300 : 60,
            }}
            transition={{ type: "spring", stiffness: 50 }}
          >
            <div className="initial-container">
              <div className="header">
                <p>Select a branch</p>
                {openBranchDropdown ? (
                  <RxCaretUp size={22} />
                ) : (
                  <RxCaretDown size={22} />
                )}
              </div>

              {selectedBranch ? (
                <div className="selected-branch">
                  <p>{selectedBranch.pharmacyName}</p>
                </div>
              ) : (
                <div className="placeholder-text">
                  <p>Please Select</p>
                </div>
              )}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{
                opacity: openBranchDropdown ? 1 : 0,
              }}
              transition={{
                ease: "easeOut",
                duration: 0.3,
                delay: openBranchDropdown ? 0.3 : 0,
              }}
              className="pharmacy-list"
            >
              {servicePharmacy?.map(
                (pharmacy, index) => (
                  <div
                    key={index}
                    className="pharmacy-item"
                    onClick={() => handlePharmacyClick(pharmacy)}
                  >
                    <div className="pharmacy-info">
                      <div className="pharmacy-icon">
                        <img src={pharmacyIcon} alt="pharmacy icon" />
                      </div>
                      <div>
                        <p className="pharmacy-name">{pharmacy.pharmacyName}</p>
                        <p className="pharmacy-address">
                          {pharmacy.pharmacyAddress}
                        </p>
                      </div>
                    </div>

                    <FiArrowRight className="arrow" />
                  </div>
                )
              )}
            </motion.div>
          </motion.div>
          <p className="note">
            *We'll make your appointment at the selected branch
          </p>
        </div>

        <div
          className={`bf-select-date ${enableDateTimeSelection ? "" : "disabled"
            }`}
        >
          <div className="bf-content-header">
            <h2 className="bf-title" style={{ color: mainColor }}>
              Select a Date
            </h2>
            {enableDateTimeSelection && (
              <p className="bf-subtitle bf-selected-branch-days">
                This branch offers this service on{" "}
                <span>{availableDays.join(", ")}</span>
              </p>
            )}
          </div>
          <Calendar
            value={date}
            onChange={(e) => handleDateSelection(e.value)}
            inline
            showWeek
            className="bf-calendar"
            disabled={enableDateTimeSelection ? "" : "disabled"}
            disabledDays={disableDays}
            minDate={new Date()}
          />
        </div>

        <div
          className={`bf-choose-time ${enableDateTimeSelection ? "" : "disabled"
            }`}
        >
          <div className="bf-content-header">
            <h2 className="bf-title" style={{ color: mainColor }}>
              Choose a time
            </h2>
            <p className="bf-subtitle">
              Appointments fill up fast, so book early to secure your spot!
            </p>
          </div>

          <div className="bf-time-slots">
            {isBookingSlotLoading ? (
              <div className="booking-loader">
                <CustomLoader width={45} height={45} />
              </div>
            ) : availableBookingSlots?.length > 0 ? (
              availableBookingSlots.map((slot, index) => (
                <div key={index} className="bf-time-slot">
                  <div className="details">
                    <p className="time">
                      {slot.startTime} - {slot.endTime}
                    </p>
                    <p className="appnt-text">
                      {slot.duration.toUpperCase()} APPOINTMENT
                    </p>
                  </div>
                  <BookNowButton selectedSlot={slot} />
                </div>
              ))
            ) : (
              <p>No slots available</p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
